import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import axios from 'axios'
import { API_URL } from '@/config/constants'

// Types
interface TourState {
  isActive: boolean
  currentStep: number
  hasCompleted: boolean
}

interface SuperAdminStore {
  globalTour: TourState
  establishmentTour: TourState
  adminTour: TourState
  addAdminTour: TourState

  startGlobalTour: () => void
  nextGlobalStep: () => void
  completeGlobalTour: () => void
  resetGlobalTour: () => void
  setGlobalStep: (step: number) => void

  startEstablishmentTour: () => void
  nextEstablishmentStep: () => void
  completeEstablishmentTour: () => void
  resetEstablishmentTour: () => void
  setEstablishmentStep: (step: number) => void

  startAdminTour: () => void
  nextAdminStep: () => void
  completeAdminTour: () => void
  resetAdminTour: () => void
  setAdminStep: (step: number) => void

  startAddAdminTour: () => void
  completeAddAdminTour: () => void

  resetAllTours: () => void
  resetAllToursToFirstTime: () => void
}

// Initial state
const initialTourState: TourState = {
  isActive: false,
  currentStep: 0,
  hasCompleted: false,
}

// API function to update onboarding status
const updateOnboarding = async (status: boolean) => {
  try {
    await axios.patch(
      `${API_URL}/user-controller/update-onboarding`,
      { userOnboarding: status },
      {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true, // Send HTTP-only cookies
      }
    )
// console.log✅ Sent userOnboarding =', status)
  } catch (err) {
    // console.error('❌ Failed to update onboarding status:', err)
  }
}

// API function to get onboarding flag
export const getUserOnboardingStatus = async (): Promise<boolean | null> => {
  try {
    const response = await axios.get(`${API_URL}/user-controller/session`, {
      withCredentials: true, // Send HTTP-only cookies
    })
    const userOnboarding = response.data?.userOnboarding
// console.log🟢 userOnboarding:', userOnboarding)
    return userOnboarding
  } catch (err) {
    // console.error('❌ Failed to fetch onboarding status:', err)
    return null
  }
}

// Helper function to check if all tours are completed
const checkIfAllToursCompleted = (state: SuperAdminStore) => {
  const allCompleted = 
    state.globalTour.hasCompleted &&
    state.establishmentTour.hasCompleted &&
    state.adminTour.hasCompleted &&
    state.addAdminTour.hasCompleted

  if (allCompleted) {
// console.log🎉 All tours completed! Updating onboarding to false')
    updateOnboarding(false)
  }
}

// Zustand Store
export const useSuperAdminStore = create<SuperAdminStore>()(
  persist(
    (set, get) => ({
      globalTour: { ...initialTourState },
      establishmentTour: { ...initialTourState },
      adminTour: { ...initialTourState },
      addAdminTour: { ...initialTourState },

      startGlobalTour: () => {
        updateOnboarding(true)
        set((state) => ({
          globalTour: {
            ...state.globalTour,
            isActive: true,
            currentStep: 0,
          },
        }))
      },

      nextGlobalStep: () =>
        set((state) => ({
          globalTour: {
            ...state.globalTour,
            currentStep: state.globalTour.currentStep + 1,
          },
        })),

      completeGlobalTour: () => {
        set((state) => ({
          globalTour: {
            ...state.globalTour,
            isActive: false,
            hasCompleted: true,
          },
        }))
        // Check if all tours are completed after updating state
        setTimeout(() => checkIfAllToursCompleted(get()), 0)
      },

      resetGlobalTour: () => {
        updateOnboarding(true)
        set(() => ({
          globalTour: { ...initialTourState },
          establishmentTour: { ...initialTourState },
          adminTour: { ...initialTourState },
        }))
      },

      setGlobalStep: (step: number) =>
        set((state) => ({
          globalTour: {
            ...state.globalTour,
            currentStep: step,
          },
        })),

      startEstablishmentTour: () =>
        set((state) => ({
          establishmentTour: {
            ...state.establishmentTour,
            isActive: true,
            currentStep: 0,
          },
        })),

      nextEstablishmentStep: () =>
        set((state) => ({
          establishmentTour: {
            ...state.establishmentTour,
            currentStep: state.establishmentTour.currentStep + 1,
          },
        })),

      completeEstablishmentTour: () => {
        set((state) => ({
          establishmentTour: {
            ...state.establishmentTour,
            isActive: false,
            hasCompleted: true,
          },
        }))
        // Check if all tours are completed after updating state
        setTimeout(() => checkIfAllToursCompleted(get()), 0)
      },

      resetEstablishmentTour: () =>
        set(() => ({
          establishmentTour: { ...initialTourState },
        })),

      setEstablishmentStep: (step: number) =>
        set((state) => ({
          establishmentTour: {
            ...state.establishmentTour,
            currentStep: step,
          },
        })),

      startAdminTour: () =>
        set((state) => ({
          adminTour: {
            ...state.adminTour,
            isActive: true,
            currentStep: 0,
          },
        })),

      nextAdminStep: () =>
        set((state) => ({
          adminTour: {
            ...state.adminTour,
            currentStep: state.adminTour.currentStep + 1,
          },
        })),

      completeAdminTour: () => {
        set((state) => ({
          adminTour: {
            ...state.adminTour,
            isActive: false,
            hasCompleted: true,
          },
        }))
        // Check if all tours are completed after updating state
        setTimeout(() => checkIfAllToursCompleted(get()), 0)
      },

      resetAdminTour: () =>
        set(() => ({
          adminTour: { ...initialTourState },
        })),

      setAdminStep: (step: number) =>
        set((state) => ({
          adminTour: {
            ...state.adminTour,
            currentStep: step,
          },
        })),

      startAddAdminTour: () =>
        set((state) => ({
          addAdminTour: {
            ...state.addAdminTour,
            isActive: true,
            currentStep: 0,
          },
        })),

      completeAddAdminTour: () => {
        set((state) => ({
          addAdminTour: {
            ...state.addAdminTour,
            isActive: false,
            hasCompleted: true,
          },
        }))
        // Check if all tours are completed after updating state
        setTimeout(() => checkIfAllToursCompleted(get()), 0)
      },

      resetAllTours: () => {
    // console.logresetAllTours: Canceling all tours...')
        updateOnboarding(true) // Set to true to hide tours
        // Reset all tours to inactive state (cancel them)
        set(() => ({
          globalTour: { isActive: false, hasCompleted: true, currentStep: 0 },
          establishmentTour: { isActive: false, hasCompleted: true, currentStep: 0 },
          adminTour: { isActive: false, hasCompleted: true, currentStep: 0 },
          addAdminTour: { isActive: false, hasCompleted: true, currentStep: 0 },
        }))
    // console.logresetAllTours: All tours canceled')
      },

      resetAllToursToFirstTime: () => {
    // console.logresetAllToursToFirstTime: Starting reset...')
        
        // Clear all localStorage items related to tours
        localStorage.removeItem('isFirstTimeUser')
        localStorage.removeItem('userOnboarding')
        localStorage.removeItem('tour_completed_establishment')
        localStorage.removeItem('tour_completed_admin_timetable')
        localStorage.removeItem('tour_skipped_establishment')
        localStorage.removeItem('tour_skipped_admin_timetable')
        localStorage.removeItem('trigger_admin_timetable_tour')
        localStorage.removeItem('tour_completed_super_admin')
        localStorage.removeItem('tour_completed_database')
        localStorage.removeItem('tour_completed_profile')
        localStorage.removeItem('super_admin_tour_active')
        localStorage.removeItem('tour_step_index')

        // Update server onboarding status - set to false to show tours
        updateOnboarding(false)

        // Reset all tour states to initial state, but activate all tours
        set(() => ({
          globalTour: { 
            isActive: true, 
            currentStep: 0, 
            hasCompleted: false
          },
          establishmentTour: { 
            isActive: true, 
            currentStep: 0, 
            hasCompleted: false
          },
          adminTour: { 
            isActive: true, 
            currentStep: 0, 
            hasCompleted: false
          },
          addAdminTour: { 
            isActive: true, 
            currentStep: 0, 
            hasCompleted: false
          }
        }))

    // console.logresetAllToursToFirstTime: All tours activated')
        
        // Force a re-render by dispatching a custom event
        window.dispatchEvent(new CustomEvent('tour-reset-completed'))
      },
    }),
    {
      name: 'super-admin-tour-storage',
    }
  )
)

// Init function - tours are now managed by useTourManager hook
export const initTourFromServer = async () => {
  // Tours are now managed by useTourManager hook
  // No need for manual initialization
}